export type UnitSystem = "metric" | "imperial";

export type VitalType = "temperature" | "weight" | "height" | "other";

export type UnitInfo = {
  vitalType: VitalType;
  originalUnit: string;
  normalizedUnit: string;
};

/**
 * Detects the type of vital and normalizes the unit code.
 * FHIR can use various unit representations (unit text, UCUM codes, etc.)
 */
export function detectVitalType(unit: string | undefined): UnitInfo {
  const unitLower = (unit ?? "").toLowerCase().replace(/[{}\s]/g, "");

  // Temperature detection
  if (unitLower === "degc" || unitLower === "c" || unitLower === "celsius") {
    return { vitalType: "temperature", originalUnit: unit ?? "", normalizedUnit: "degC" };
  }
  if (unitLower === "degf" || unitLower === "f" || unitLower === "fahrenheit") {
    return { vitalType: "temperature", originalUnit: unit ?? "", normalizedUnit: "degF" };
  }

  // Weight detection
  if (unitLower === "kg" || unitLower === "kilogram" || unitLower === "kilograms") {
    return { vitalType: "weight", originalUnit: unit ?? "", normalizedUnit: "kg" };
  }
  if (
    unitLower === "lb" ||
    unitLower === "lbs" ||
    unitLower === "pound" ||
    unitLower === "pounds" ||
    unitLower === "[lb_av]"
  ) {
    return { vitalType: "weight", originalUnit: unit ?? "", normalizedUnit: "lb" };
  }

  // Height detection
  if (unitLower === "cm" || unitLower === "centimeter" || unitLower === "centimeters") {
    return { vitalType: "height", originalUnit: unit ?? "", normalizedUnit: "cm" };
  }
  if (unitLower === "m" || unitLower === "meter" || unitLower === "meters") {
    return { vitalType: "height", originalUnit: unit ?? "", normalizedUnit: "m" };
  }
  if (
    unitLower === "in" ||
    unitLower === "inch" ||
    unitLower === "inches" ||
    unitLower === "[in_i]"
  ) {
    return { vitalType: "height", originalUnit: unit ?? "", normalizedUnit: "in" };
  }
  if (unitLower === "ft" || unitLower === "foot" || unitLower === "feet") {
    return { vitalType: "height", originalUnit: unit ?? "", normalizedUnit: "ft" };
  }

  return { vitalType: "other", originalUnit: unit ?? "", normalizedUnit: unit ?? "" };
}

/**
 * Converts temperature between Celsius and Fahrenheit
 */
export function convertTemperature(value: number, fromUnit: string, toUnit: string): number {
  const from = fromUnit.toLowerCase().replace(/[{}\s]/g, "");
  const to = toUnit.toLowerCase().replace(/[{}\s]/g, "");

  if (from === to) return value;

  // Convert to Celsius first if needed
  let celsius = value;
  if (from === "degf" || from === "f" || from === "fahrenheit") {
    celsius = ((value - 32) * 5) / 9;
  }

  // Convert to target unit
  if (to === "degf" || to === "f" || to === "fahrenheit") {
    return (celsius * 9) / 5 + 32;
  }

  return celsius;
}

/**
 * Converts weight between kilograms and pounds
 */
export function convertWeight(value: number, fromUnit: string, toUnit: string): number {
  const from = fromUnit.toLowerCase().replace(/[{}\s[\]]/g, "");
  const to = toUnit.toLowerCase().replace(/[{}\s[\]]/g, "");

  if (from === to) return value;

  // Convert to kg first if needed
  let kg = value;
  if (
    from === "lb" ||
    from === "lbs" ||
    from === "pound" ||
    from === "pounds" ||
    from === "lb_av"
  ) {
    kg = value / 2.20462;
  }

  // Convert to target unit
  if (to === "lb" || to === "lbs" || to === "pound" || to === "pounds" || to === "lb_av") {
    return kg * 2.20462;
  }

  return kg;
}

/**
 * Converts height between metric (cm/m) and imperial (ft/in)
 */
export function convertHeight(value: number, fromUnit: string, toUnit: string): number {
  const from = fromUnit.toLowerCase().replace(/[{}\s[\]]/g, "");
  const to = toUnit.toLowerCase().replace(/[{}\s[\]]/g, "");

  if (from === to) return value;

  // Convert to cm first
  let cm = value;
  if (from === "m" || from === "meter" || from === "meters") {
    cm = value * 100;
  } else if (from === "in" || from === "inch" || from === "inches" || from === "in_i") {
    cm = value * 2.54;
  } else if (from === "ft" || from === "foot" || from === "feet") {
    cm = value * 30.48;
  }

  // Convert to target unit
  if (to === "m" || to === "meter" || to === "meters") {
    return cm / 100;
  } else if (to === "in" || to === "inch" || to === "inches" || to === "in_i") {
    return cm / 2.54;
  } else if (to === "ft" || to === "foot" || to === "feet") {
    return cm / 30.48;
  }

  return cm;
}

/**
 * Formats height in feet and inches for imperial display
 */
export function formatHeightImperial(totalInches: number): string {
  const feet = Math.floor(totalInches / 12);
  const inches = Math.round(totalInches % 12);
  return `${feet}' ${inches}"`;
}

/**
 * Converts and formats a vital value based on the target unit system
 */
export function convertAndFormatVital(
  value: number,
  unitInfo: UnitInfo,
  targetSystem: UnitSystem
): string {
  const { vitalType, normalizedUnit } = unitInfo;

  if (vitalType === "temperature") {
    if (targetSystem === "metric") {
      const celsius = convertTemperature(value, normalizedUnit, "degC");
      return `${celsius.toFixed(1)} °C`;
    } else {
      const fahrenheit = convertTemperature(value, normalizedUnit, "degF");
      return `${fahrenheit.toFixed(1)} °F`;
    }
  }

  if (vitalType === "weight") {
    if (targetSystem === "metric") {
      const kg = convertWeight(value, normalizedUnit, "kg");
      return `${kg.toFixed(1)} kg`;
    } else {
      const lbs = convertWeight(value, normalizedUnit, "lb");
      return `${lbs.toFixed(1)} lbs`;
    }
  }

  if (vitalType === "height") {
    if (targetSystem === "metric") {
      const cm = convertHeight(value, normalizedUnit, "cm");
      return `${cm.toFixed(1)} cm`;
    } else {
      const inches = convertHeight(value, normalizedUnit, "in");
      return formatHeightImperial(inches);
    }
  }

  // For other vitals, return original value with unit
  return `${value} ${unitInfo.originalUnit}`;
}
